import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

/**
 * ProfileReminderGuard (formerly SquidTourGuard)
 * 
 * UPDATED Jan 2026: SquidTour has been SUSPENDED
 * This component now shows a gentle reminder for users to complete their profile
 * 
 * Features:
 * - Shows dismissible profile completion reminder
 * - Only shows if profile is incomplete (missing bio, avatar, or career info)
 * - Admin bypass
 * - Persists dismissal in localStorage
 */
const SquidTourGuard = ({ children, user: propUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, loading, isAdmin } = useAuth();
  const user = authUser || propUser;
  
  const [showReminder, setShowReminder] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem('profile_reminder_dismissed') === 'true';
  });

  // Pages where we don't show the reminder
  const excludedPaths = [
    '/profile',           // They're already on profile
    '/login',
    '/register', 
    '/logout',
    '/confirm-email',
    '/auth/callback',
    '/squidboard',        // Admin area
  ];

  // Check if current path should show reminder
  const shouldShowOnPath = !excludedPaths.some(path => 
    location.pathname === path || location.pathname.startsWith(path + '/')
  );

  // Check if profile is incomplete
  const isProfileIncomplete = (userData) => {
    if (!userData) return false;
    
    // Check for missing key profile fields
    const missingBio = !userData.bio || userData.bio.trim().length < 10;
    const missingAvatar = !userData.profile_picture || userData.profile_picture.includes('default');
    const missingCareer = !userData.career_path && !userData.career_goal;
    const missingLocation = !userData.city && !userData.country;
    
    // Show reminder if any 2 or more fields are missing
    const missingCount = [missingBio, missingAvatar, missingCareer, missingLocation].filter(Boolean).length;
    return missingCount >= 2;
  };

  useEffect(() => {
    // Show reminder after a delay if conditions are met
    if (user && !isAdmin && !dismissed && shouldShowOnPath && isProfileIncomplete(user)) {
      const timer = setTimeout(() => {
        setShowReminder(true);
      }, 5 * 60 * 1000); // Show after 5 minutes
      
      return () => clearTimeout(timer);
    }
  }, [user, isAdmin, dismissed, shouldShowOnPath, location.pathname]);

  const handleDismiss = () => {
    localStorage.setItem('profile_reminder_dismissed', 'true');
    setDismissed(true);
    setShowReminder(false);
  };

  const handleDismissTemporary = () => {
    // Just hide for this session
    setShowReminder(false);
  };

  const handleGoToProfile = () => {
    setShowReminder(false);
    navigate('/profile');
  };

  // Always render children - no blocking
  return (
    <>
      {children}
      
      {/* Profile Completion Reminder Banner */}
      <AnimatePresence>
        {showReminder && user && !dismissed && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-[998]"
          >
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 border border-cyan-500/30 text-white p-4 rounded-xl shadow-2xl">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm text-white flex items-center gap-2">
                    Complete Your Profile
                    <Sparkles className="w-4 h-4 text-amber-400" />
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Add your bio, career goals, and profile picture to get personalized recommendations and connect with mentors.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      className="bg-cyan-600 hover:bg-cyan-700 text-xs"
                      onClick={handleGoToProfile}
                    >
                      Update Profile
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-slate-400 hover:text-white text-xs"
                      onClick={handleDismiss}
                    >
                      Don't Show Again
                    </Button>
                  </div>
                </div>
                <button 
                  onClick={handleDismissTemporary}
                  className="text-slate-500 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SquidTourGuard;
